 /* tt-custom-free-shipping-bar */
import { ThemeEvents } from '@theme/events';

class CartFreeShippingProgress extends HTMLElement {
  /** @type {AbortController | null} */
  #controller = null;

  connectedCallback() {
    this.#controller = new AbortController();
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdate.bind(this), {
      signal: /** @type {AbortController} */ (this.#controller).signal,
    });
  }

  disconnectedCallback() {
    if (this.#controller) {
      this.#controller.abort();
      this.#controller = null;
    }
  }

  #onCartUpdate(event) {
    const totalPrice = event?.detail?.resource?.total_price;
    if (typeof totalPrice !== 'number') return;
    this.dataset.cartTotalCents = String(totalPrice);
    this.#syncBar();
  }

  #syncBar() {
    const total = parseInt(this.dataset.cartTotalCents || '0', 10);
    const threshold = parseInt(this.dataset.thresholdCents || '0', 10);
    if (threshold <= 0) return;

    const fill = this.querySelector('.cart-free-shipping__fill');
    const track = this.querySelector('.cart-free-shipping__track');
    if (!fill || !track) return;

    const pct = Math.min(100, Math.round((total / threshold) * 100));
    fill.style.setProperty('--free-shipping-fill-scale', String(pct / 100));
    track.setAttribute('aria-valuenow', String(pct));

    // A cart update can arrive before its containing section is re-rendered.
    // Never leave the below-threshold delivery note visible after free shipping
    // has been unlocked.
    this.querySelector('.cart-free-shipping__additional-message')?.toggleAttribute('hidden', total >= threshold);
  }
}

if (!customElements.get('cart-free-shipping-progress')) {
  customElements.define('cart-free-shipping-progress', CartFreeShippingProgress);
}

class CartCouponShowcase extends HTMLElement {
  #copyFeedbackTimeout = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
    if (this.#copyFeedbackTimeout) clearTimeout(this.#copyFeedbackTimeout);
  }

  #handleClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-copy-code]') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    const code = button.dataset.copyCode;
    if (!code) return;

    const copied = await copyToClipboard(code);
    if (copied) this.#showCopyFeedback(button, code);
  };

  #showCopyFeedback(button, code) {
    if (this.#copyFeedbackTimeout) clearTimeout(this.#copyFeedbackTimeout);

    button.classList.add('is-copied');
    button.setAttribute('aria-label', `${code} copied`);

    this.#copyFeedbackTimeout = setTimeout(() => {
      button.classList.remove('is-copied');
      button.setAttribute('aria-label', `Copy coupon code ${code}`);
      this.#copyFeedbackTimeout = null;
    }, 2000);
  }
}

async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText && window.isSecureContext) {
      await navigator.clipboard.writeText(text);
      return true;
    }

    const input = document.createElement('textarea');
    input.value = text;
    input.setAttribute('readonly', '');
    input.style.cssText = 'position:fixed;opacity:0;pointer-events:none';
    document.body.append(input);
    input.select();
    const copied = document.execCommand('copy');
    input.remove();
    return copied;
  } catch {
    return false;
  }
}

if (!customElements.get('cart-coupon-showcase')) {
  customElements.define('cart-coupon-showcase', CartCouponShowcase);
}
 /* tt-custom-free-shipping-bar */
